import { translateClient } from '@/lib/i18n/client';

const FETCH_TIMEOUT = 5000;
export const UPDATE_SNOOZE_MS = 10 * 60 * 1000;
const SNOOZE_LIMIT_PER_BUILD = 1;
const SNOOZE_STORAGE_KEY = 'dobi-client-update-snooze';
export const BEFORE_CLIENT_REFRESH_EVENT = 'dobi:before-client-refresh';

export interface VersionInfo {
  buildId: string;
  buildTime: string;
}

export interface VersionState {
  currentBuildId: string | null;
  latestBuildId: string | null;
  latestBuildTime: string | null;
  isStale: boolean;
  detectedAt: number | null;
  lastCheckedAt: number | null;
  checkStatus: 'unchecked' | 'confirmed' | 'failed';
  lastConfirmedAt: number | null;
  snoozedUntil: number | null;
  snoozeCount: number;
  blockingReason: string | null;
  refreshGateOpenedAt: number | null;
  refreshGateSignal: number;
}

interface SnoozeRecord {
  buildId: string;
  count: number;
  until: number;
}

const buildIdFromBundle = process.env.NEXT_PUBLIC_DOBI_BUILD_ID || null;
const buildTimeFromBundle = process.env.NEXT_PUBLIC_DOBI_BUILD_TIME || null;

let versionState: VersionState = {
  currentBuildId: buildIdFromBundle,
  latestBuildId: null,
  latestBuildTime: null,
  isStale: false,
  detectedAt: null,
  lastCheckedAt: null,
  checkStatus: 'unchecked',
  lastConfirmedAt: null,
  snoozedUntil: null,
  snoozeCount: 0,
  blockingReason: null,
  refreshGateOpenedAt: null,
  refreshGateSignal: 0,
};

let pendingCheck: Promise<VersionState> | null = null;
const listeners = new Set<(state: VersionState) => void>();

export class ClientVersionStaleError extends Error {
  constructor(message = 'Client version is stale') {
    super(message);
    this.name = 'ClientVersionStaleError';
  }
}

function isDevMode(): boolean {
  return process.env.NODE_ENV === 'development';
}

function cloneState(): VersionState {
  return { ...versionState };
}

function emitVersionState(): void {
  const snapshot = cloneState();
  listeners.forEach(listener => listener(snapshot));
}

function setVersionState(next: Partial<VersionState>): VersionState {
  versionState = { ...versionState, ...next };
  emitVersionState();
  return cloneState();
}

function readSnoozeRecord(buildId: string | null): SnoozeRecord | null {
  if (typeof window === 'undefined' || !buildId) return null;
  try {
    const raw = window.localStorage.getItem(SNOOZE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SnoozeRecord>;
    if (parsed.buildId !== buildId) return null;
    return {
      buildId,
      count: Number(parsed.count) || 0,
      until: Number(parsed.until) || 0,
    };
  } catch {
    return null;
  }
}

function writeSnoozeRecord(record: SnoozeRecord): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage can be unavailable in private browsing; the in-memory state still works.
  }
}

function syncSnoozeForBuild(buildId: string | null): Pick<VersionState, 'snoozedUntil' | 'snoozeCount'> {
  const record = readSnoozeRecord(buildId);
  const now = Date.now();
  if (!record) {
    return { snoozedUntil: null, snoozeCount: 0 };
  }
  return {
    snoozedUntil: record.until > now ? record.until : null,
    snoozeCount: Math.max(0, record.count),
  };
}

function notifyUpdateRequired(reason = 'critical-action'): VersionState {
  const next = setVersionState({
    blockingReason: reason,
    refreshGateOpenedAt: Date.now(),
    refreshGateSignal: versionState.refreshGateSignal + 1,
  });

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('dobi:client-version-stale', { detail: next }));
  }
  return next;
}

export function getVersionState(): VersionState {
  return cloneState();
}

/** A default false stale flag is not evidence that a version check succeeded. */
export function getClientVersionDiagnostics(): Record<string, unknown> {
  const state = getVersionState();
  const data = typeof window === 'undefined' ? undefined
    : (window as Window & {__NEXT_DATA__?: {buildId?: unknown}}).__NEXT_DATA__;
  return {
    clientVersionCheckStatus: state.checkStatus,
    clientVersionLastAttemptAt: state.lastCheckedAt,
    clientVersionLastConfirmedAt: state.lastConfirmedAt,
    clientVersionComparison: !state.currentBuildId || !state.latestBuildId || state.checkStatus !== 'confirmed'
      ? 'unknown' : state.isStale ? 'stale' : 'current',
    clientNextBuildId: typeof data?.buildId === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(data.buildId)
      ? data.buildId : null,
  };
}

export function subscribeVersionState(listener: (state: VersionState) => void): () => void {
  listeners.add(listener);
  listener(cloneState());
  return () => listeners.delete(listener);
}

export function isClientUpdateSnoozed(now = Date.now()): boolean {
  return Boolean(versionState.snoozedUntil && versionState.snoozedUntil > now);
}

export function canSnoozeClientUpdate(): boolean {
  if (!versionState.isStale || !versionState.latestBuildId) return false;
  return versionState.snoozeCount < SNOOZE_LIMIT_PER_BUILD;
}

export function snoozeClientUpdate(): boolean {
  if (!canSnoozeClientUpdate() || !versionState.latestBuildId) return false;

  const record: SnoozeRecord = {
    buildId: versionState.latestBuildId,
    count: versionState.snoozeCount + 1,
    until: Date.now() + UPDATE_SNOOZE_MS,
  };

  writeSnoozeRecord(record);
  setVersionState({
    snoozedUntil: record.until,
    snoozeCount: record.count,
  });
  return true;
}

export function requestClientRefreshGate(reason = 'manual'): VersionState {
  return notifyUpdateRequired(reason);
}

export function clearClientRefreshGate(): VersionState {
  return setVersionState({
    blockingReason: null,
    refreshGateOpenedAt: null,
  });
}

export async function prepareForClientRefresh(timeoutMs = 3000): Promise<void> {
  if (typeof window === 'undefined') return;

  const pending: Promise<unknown>[] = [];
  const detail = {
    addPromise: (promise: Promise<unknown>) => {
      pending.push(promise);
    },
  };

  window.dispatchEvent(new CustomEvent(BEFORE_CLIENT_REFRESH_EVENT, { detail }));
  if (pending.length === 0) return;

  await Promise.race([
    Promise.allSettled(pending),
    new Promise(resolve => window.setTimeout(resolve, timeoutMs)),
  ]);
}

export async function refreshClientWhenReady(timeoutMs = 3000): Promise<void> {
  if (typeof window === 'undefined') return;
  await prepareForClientRefresh(timeoutMs);
  window.location.reload();
}

export function getClientBuildHeaders(): Record<string, string> {
  const buildId = versionState.currentBuildId || buildIdFromBundle || versionState.latestBuildId;
  return {
    ...(buildId ? { 'X-Dobi-Client-Build': buildId } : {}),
    'X-Dobi-Client-Stale': versionState.isStale ? 'true' : 'false',
  };
}

export function isCriticalClientMutation(method?: string): boolean {
  const normalized = (method || 'GET').toUpperCase();
  return !['GET', 'HEAD', 'OPTIONS'].includes(normalized);
}

export async function checkClientVersion(options: { force?: boolean } = {}): Promise<VersionState> {
  if (typeof window === 'undefined' || isDevMode()) return cloneState();
  // A forced preflight still shares an already fresh in-flight check. Starting
  // a second request allows an older response to overwrite the newer result.
  if (pendingCheck) return pendingCheck;

  pendingCheck = (async () => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!res.ok) return setVersionState({ lastCheckedAt: Date.now(), checkStatus: 'failed' });

      const data = await res.json() as VersionInfo;
      if (!data || typeof data.buildId !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(data.buildId)
        || typeof data.buildTime !== 'string' || !Number.isFinite(Date.parse(data.buildTime))) {
        return setVersionState({lastCheckedAt: Date.now(), checkStatus: 'failed'});
      }
      const currentBuildId = versionState.currentBuildId;
      const isStale = Boolean(currentBuildId && data.buildId && data.buildId !== currentBuildId);
      const snooze = isStale ? syncSnoozeForBuild(data.buildId) : { snoozedUntil: null, snoozeCount: 0 };

      return setVersionState({
        currentBuildId,
        latestBuildId: data.buildId,
        latestBuildTime: data.buildTime,
        isStale,
        detectedAt: isStale ? (versionState.detectedAt ?? Date.now()) : null,
        lastCheckedAt: Date.now(),
        lastConfirmedAt: Date.now(),
        checkStatus: 'confirmed',
        blockingReason: isStale ? versionState.blockingReason : null,
        refreshGateOpenedAt: isStale ? versionState.refreshGateOpenedAt : null,
        ...snooze,
      });
    } catch {
      return setVersionState({ lastCheckedAt: Date.now(), checkStatus: 'failed' });
    } finally {
      window.clearTimeout(timer);
      pendingCheck = null;
    }
  })();

  return pendingCheck;
}

export async function assertFreshClientBeforeCriticalAction(): Promise<void> {
  if (typeof window === 'undefined' || isDevMode()) return;

  const state = await checkClientVersion({ force: true });
  if (!state.isStale) return;

  notifyUpdateRequired('critical-action');
  throw new ClientVersionStaleError(translateClient('common', 'refreshRequiredMessage'));
}
